import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { apiClient } from '../lib/apiClient'
import styles from './OnboardingPage.module.css'

export default function OnboardingPage() {
  const { user, refreshOrg, signOut, organization } = useAuth()
  const navigate = useNavigate()
  const [step, setStep] = useState<'choice' | 'create' | 'join'>('choice')
  const [orgName, setOrgName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [currency, setCurrency] = useState<'USD' | 'GBP' | 'INR'>('USD')
  const [inviteCode, setInviteCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [checkingOrg, setCheckingOrg] = useState(true)

  React.useEffect(() => {
    const checkExistingOrg = async () => {
      console.log('[Onboarding] Checking for existing organization...')
      setCheckingOrg(true)
      await refreshOrg()
      setCheckingOrg(false)
    }
    checkExistingOrg()
  }, [])

  React.useEffect(() => {
    if (organization && !checkingOrg) {
      console.log('[Onboarding] Organization found, redirecting to app...')
      navigate('/app')
    }
  }, [organization, checkingOrg, navigate])

  const handleRefresh = async () => {
    setRefreshing(true)
    setError('')
    try {
      await refreshOrg()
      if (organization) {
        navigate('/app')
      } else {
        setError('No organization found. Please create one or contact your admin.')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load organization')
    } finally {
      setRefreshing(false)
    }
  }

  const slugify = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgName.trim() || !displayName.trim()) return
    setLoading(true)
    setError('')
    try {
      const slug = slugify(orgName) + '-' + Math.random().toString(36).slice(2, 7)

      const data = await apiClient.post<{ id: string }>('/organizations', {
        name: orgName.trim(),
        slug,
        defaultCurrency: currency,
        displayName: displayName.trim(),
      })

      if (!data) throw new Error('Failed to create organization')

      await refreshOrg()
      navigate('/')
    } catch (err: any) {
      setError(err.message || 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  if (checkingOrg) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, flexDirection: 'column', gap: 16, color: '#605e5c' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #edebe9', borderTopColor: '#0078d4', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <p style={{ margin: 0, fontSize: 14 }}>Checking for existing organization...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <div className={styles.brandIcon}>AQ</div>
          <span className={styles.brandName}>allocatIQ</span>
        </div>

        <h1 className={styles.title}>Welcome{user?.email ? `, ${user.email.split('@')[0]}` : ''}!</h1>
        <p className={styles.subtitle}>You need to set up a workspace before continuing.</p>

        <div style={{ marginBottom: 16 }}>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              border: '1px solid #0078d4',
              background: 'white',
              color: '#0078d4',
              borderRadius: 4,
              cursor: refreshing ? 'not-allowed' : 'pointer',
              opacity: refreshing ? 0.6 : 1
            }}
          >
            {refreshing ? 'Checking...' : 'Check for Organization'}
          </button>
        </div>

        {step === 'choice' && (
          <div className={styles.choices}>
            <button className={styles.choiceBtn} onClick={() => setStep('create')}>
              <span className={styles.choiceIcon}>+</span>
              <div>
                <div className={styles.choiceTitle}>Create a new organization</div>
                <div className={styles.choiceDesc}>Set up a new workspace for your team</div>
              </div>
            </button>
            <button className={styles.choiceBtn} onClick={() => setStep('join')}>
              <span className={styles.choiceIcon}>→</span>
              <div>
                <div className={styles.choiceTitle}>Join an existing organization</div>
                <div className={styles.choiceDesc}>Ask your admin to add you as a member</div>
              </div>
            </button>
          </div>
        )}

        {step === 'join' && (
          <div>
            <div style={{ padding: '16px', background: '#deecf9', borderRadius: 4, marginBottom: 20, fontSize: 13, color: '#323130', lineHeight: 1.6 }}>
              <strong>How to join an existing organization:</strong>
              <ol style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                <li>Contact your organization's admin</li>
                <li>Ask them to go to Admin Panel → Users & Roles</li>
                <li>They can add you directly using your email address: <strong>{user?.email}</strong></li>
              </ol>
            </div>
            <div className={styles.btnRow} style={{ justifyContent: 'flex-start' }}>
              <button type="button" className={styles.backBtn} onClick={() => setStep('choice')}>← Back</button>
            </div>
          </div>
        )}

        {step === 'create' && (
          <form onSubmit={handleCreateOrg} className={styles.form}>
            <div className={styles.formGroup}>
              <label>Organization Name *</label>
              <input
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
                placeholder="Acme Corp"
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label>Your Display Name *</label>
              <input
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder="Jane Smith"
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label>Default Currency</label>
              <select value={currency} onChange={e => setCurrency(e.target.value as any)}>
                <option value="USD">USD — US Dollar</option>
                <option value="GBP">GBP — British Pound</option>
                <option value="INR">INR — Indian Rupee</option>
              </select>
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <div className={styles.btnRow}>
              <button type="button" className={styles.backBtn} onClick={() => setStep('choice')}>Back</button>
              <button type="submit" className={styles.submitBtn} disabled={loading}>
                {loading ? 'Creating...' : 'Create Organization'}
              </button>
            </div>
          </form>
        )}

        <button className={styles.signOutLink} onClick={signOut}>Sign out</button>
      </div>
    </div>
  )
}
